import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatTab, MatTabHeader } from '@angular/material/tabs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { ConvertedIntentTopicInterface } from '../../models/converted-intent-topic.interface';
import { AddonsIdentifiersEnum, FeatureFlagsEnum, IntentSourceEnum } from 'src/app/shared/enums';
import { AccountIntentTopic, IntentTopic, IntentTopicAutomationMapping, IntentTopicIds, IntentTopicsDto } from 'src/app/shared/dto';
import { AccountService, CommonUtilService, FeatureFlagService, LoadingService } from 'src/app/shared/services';
import { LoadingWrapper } from 'src/app/shared/decorators';
import { GeneralsMenuTabsEnum } from 'src/app/modals/intent-settings/enums/general-menu-tabs.enum';
import { DeleteTopicConfirmationComponent } from 'src/app/shared/modules/intent/components/delete-topic-confirmation/delete-topic-confirmation.component';
import { OrderByPipe } from 'src/app/shared/pipes';
import {  MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BaseTabStateChangeDetection, FormChangeStateService, UnsavedChangesWarningDialogResponse } from '../../../../components';
import { INTENT_SETTING_MODAL_LOADING_TAG } from 'src/app/modals/intent-settings/constants';
import { UserService } from 'src/app/core/services';
import { User } from 'src/app/core/models/user.model';
import { INTENT_RECOMMENDATIONS_ENABLED } from '../../constants';

const LOADING_TAG: string = 'account-intent-loader';

@UntilDestroy()
@Component({
    selector: 'app-account-intent',
    templateUrl: './account-intent.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [OrderByPipe],
    standalone: false
})
export class AccountIntentComponent extends BaseTabStateChangeDetection implements OnInit {
    @Input() set saveIntentEvent(value: symbol) {
        if (value) {
            this.checkTopicIds();
        }
    }
    @Input() set activeTab(value: number) {
        this.chooseTab(value);
    }
    @Input() isLoading: boolean;
    @Output() tabChange: EventEmitter<GeneralsMenuTabsEnum | number> = new EventEmitter();
    @Output() dataSaved: EventEmitter<void> = new EventEmitter<void>();

    bomboraPlans;
    bomboraPropertyValue;
    bomboraTopics: ConvertedIntentTopicInterface[];
    bomboraAccountTopics: AccountIntentTopic[];
    bomboraAccountTopicsLastSaved: AccountIntentTopic[];
    salesintelPlans;
    salesintelPropertyValue;
    salesintelTopics: ConvertedIntentTopicInterface[];
    salesintelAccountTopics: AccountIntentTopic[] = [];
    salesintelAccountTopicsLastSaved: AccountIntentTopic[] = [];
    selectedTab: number = 0;
    isAdmin$: Observable<boolean>;
    // Which source's recommendation wizard is open (null = none).
    recommendationWizardSource: IntentSourceEnum.BOMBORA | IntentSourceEnum.SALES_INTEL | null = null;
    readonly recommendationsEnabled: boolean = INTENT_RECOMMENDATIONS_ENABLED;

    private tabs: GeneralsMenuTabsEnum[] = [];
    // Set when "Get recommendations" is clicked with unsaved main changes and the user chooses Save:
    // holds the source whose wizard should open once that save completes.
    private pendingWizardSource: IntentSourceEnum.BOMBORA | IntentSourceEnum.SALES_INTEL | null = null;

    private isAccountTopicsSetSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    readonly isLoading$: Observable<boolean> = this.loadingService.getIsLoading(LOADING_TAG);
    readonly intentSourceEnum: typeof IntentSourceEnum = IntentSourceEnum;
    readonly featureFlagsEnum: typeof FeatureFlagsEnum = FeatureFlagsEnum;

    constructor(
        private readonly accountService: AccountService,
        private readonly toastr: ToastrService,
        private readonly loadingService: LoadingService,
        private readonly featureFlagService: FeatureFlagService,
        private readonly matDialog: MatDialog,
        protected readonly formChangeStateService: FormChangeStateService,
        private readonly orderByPipe: OrderByPipe,
        private readonly userService: UserService,
        private readonly cdr: ChangeDetectorRef,
        private readonly commonUtilService: CommonUtilService
    ) {
        super(formChangeStateService);
    }

    ngOnInit(): void {
        this.featureFlagService.getFlag(FeatureFlagsEnum.IntentAutomations).pipe(take(1),
            untilDestroyed(this)).subscribe((isIntentAutomationsEnabled: boolean) => {
                this.tabs = isIntentAutomationsEnabled ?
                    [GeneralsMenuTabsEnum.PREDICTIVEINTENT, GeneralsMenuTabsEnum.BOMBORA_INTENT] :
                    [GeneralsMenuTabsEnum.BOMBORA_INTENT, GeneralsMenuTabsEnum.PREDICTIVEINTENT];
                this.formChangeStateService.setFormValidity(isIntentAutomationsEnabled);
                this.getAccountAddOns();
                this.setTopics();
                this.setAccountTopics(isIntentAutomationsEnabled);
            });

        this.isAdmin$ = combineLatest([
            this.userService.listenToUserChangesByKey<boolean>('is_admin'),
            this.userService.listenToUserChangesByKey<boolean>('is_ops_admin')
        ]).pipe(
            map(([isAdmin, isOpsAdmin]) => isAdmin || isOpsAdmin)
        );
        this.formChangeStateService.getSaveForm()
            .pipe(untilDestroyed(this))
            .subscribe(() => this.checkTopicIds());
    }

    onSelect(tab: GeneralsMenuTabsEnum | number) {
        if (this.isRecommendationWizardOpen) {
            this.closeRecommendationWizard();
        }
        let value: GeneralsMenuTabsEnum | number = tab;
        if (typeof tab === 'number') {
            this.selectedTab = tab;
            value = this.tabs[tab];
        } else {
            this.selectedTab = this.tabs.indexOf(tab);
        }
        this.tabChange.emit(value);
    }

    chooseTab(value: number) {
        if (value !== undefined && value !== null && value !== -1) {
            this.selectedTab = value;
        } else if (this.accountService.getIntentTabNumber() !== null) {
            this.selectedTab = this.accountService.getIntentTabNumber();
            this.accountService.setIntentTabNumber(null);
        } else {
            this.featureFlagService.getFlag(FeatureFlagsEnum.IntentAutomations).pipe(
                take(1),
                switchMap((isIntentAutomationsEnabled: boolean) => {
                    if (isIntentAutomationsEnabled) {
                        return this.isAccountTopicsSetSubject.pipe(
                            filter(Boolean),
                            take(1),
                            map(() => {
                                if (this.salesintelAccountTopics.length !== 0 || (this.salesintelAccountTopics.length === 0 && this.bomboraAccountTopics.length === 0)) {
                                    return GeneralsMenuTabsEnum.PREDICTIVEINTENT;
                                } else {
                                    return GeneralsMenuTabsEnum.BOMBORA_INTENT;
                                }
                            })
                        );
                    } else {
                        return of(null);
                    }
                }),
                filter(Boolean),
                untilDestroyed(this)
            ).subscribe((tabEnum: GeneralsMenuTabsEnum) => {
                this.onSelect(tabEnum);
            });
        }
    }

    checkTopicIds() {
        combineLatest([
            this.featureFlagService.getFlag(FeatureFlagsEnum.IntentAutomations),
            this.featureFlagService.getFlag(FeatureFlagsEnum.BomboraIntentAutomations)
        ]).pipe(
            take(1),
            switchMap(([isIntentAutomationsEnabled, isBomboraIntentAutomationsEnabled]: [boolean, boolean]) => {
                const isBomboraTab = this.selectedTab === this.tabs.indexOf(GeneralsMenuTabsEnum.BOMBORA_INTENT);

                if ((isIntentAutomationsEnabled && !isBomboraTab) || (isBomboraIntentAutomationsEnabled && isBomboraTab)) {
                    const topics: IntentTopicIds = {
                        "topicIds": isBomboraTab
                            ? this.bomboraAccountTopicsLastSaved.filter((topic1: AccountIntentTopic) => !this.bomboraAccountTopics.some((topic2: AccountIntentTopic) => topic1.topic_id === topic2.topic_id)).map((topic: AccountIntentTopic) => topic.topic_id)
                            : this.salesintelAccountTopicsLastSaved.filter((topic1: AccountIntentTopic) => !this.salesintelAccountTopics.some((topic2: AccountIntentTopic) => topic1.topic_id === topic2.topic_id)).map((topic: AccountIntentTopic) => topic.topic_id)
                    };

                    return this.getIntentAutomationTopicMappingDetails(topics).pipe(
                        switchMap((automationTopicMapping: IntentTopicAutomationMapping[]) => {
                            if (automationTopicMapping && automationTopicMapping.length !== 0) {
                                const uniqueTopicIds = new Set(automationTopicMapping.map((mapping: IntentTopicAutomationMapping) => mapping.topic_id));
                                const intentModalData: MatDialogConfig = {
                                    panelClass: 'modal-md',
                                    data: {
                                        topicNames: Array.from(uniqueTopicIds).map((topic_id: number) => {
                                            const matchItem = isBomboraTab
                                                ? this.bomboraAccountTopicsLastSaved.find((topic: AccountIntentTopic) => topic.topic_id === topic_id)
                                                : this.salesintelAccountTopicsLastSaved.find((topic: AccountIntentTopic) => topic.topic_id === topic_id);
                                            return matchItem ? matchItem.topic_name : null;
                                        }).filter(Boolean).join(', ')
                                    },
                                };
                                return this.matDialog.open(DeleteTopicConfirmationComponent, intentModalData).afterClosed();
                            }
                            return of(null);
                        }),
                        untilDestroyed(this)
                    );
                } else {
                    return of(null);
                }
            }),
            untilDestroyed(this)
        ).subscribe((res: { success: boolean }) => {
            if (res && !res.success) {
                // Save was aborted at the topic-mapping confirmation — don't auto-open the wizard.
                this.pendingWizardSource = null;
                if (this.selectedTab === this.tabs.indexOf(GeneralsMenuTabsEnum.BOMBORA_INTENT)) {
                    this.bomboraAccountTopics = [...this.bomboraAccountTopicsLastSaved];
                } else {
                    this.salesintelAccountTopics = [...this.salesintelAccountTopicsLastSaved];
                }
                this.cdr.detectChanges();
                this.checkTopicsChanged();
                return;
            }
            this.onSave();
        });
    }

    onSave(): void {
        const topics: AccountIntentTopic[] = [
            ...this.salesintelAccountTopics,
            ...this.bomboraAccountTopics,
        // @ts-ignore
        ].map(({ selected, ...topic }: AccountIntentTopic) => topic);
        this.featureFlagService.getFlag(FeatureFlagsEnum.IntentAutomations).pipe(
            take(1),
            switchMap((isIntentAutomationsEnabled: boolean) => this.postAccountIntentTopics(topics, isIntentAutomationsEnabled)))
            .subscribe((res: { response: string }) => {
                this.formChangeStateService.setFormChanged(false);
                this.toastr.success(res.response);
                this.accountService.setIntentTopics(topics);
                this.dataSaved.emit();
                if (this.pendingWizardSource) {
                    const source = this.pendingWizardSource;
                    this.pendingWizardSource = null;
                    this.setRecommendationWizardSource(source);
                }
            });
    }

    get isRecommendationWizardOpen(): boolean {
        return this.recommendationWizardSource !== null;
    }

    openRecommendationWizard(source: IntentSourceEnum.BOMBORA | IntentSourceEnum.SALES_INTEL): void {
        // Never enter the recommender with unsaved main-window changes: prompt to save/discard first,
        // then open the wizard from a clean state (so the only pending changes are the wizard's own).
        if (!this.formChangeStateService.isFormChanged()) {
            this.setRecommendationWizardSource(source);
            return;
        }
        this.formChangeStateService.showWarning()
            .pipe(take(1), untilDestroyed(this))
            .subscribe((response: UnsavedChangesWarningDialogResponse) => {
                if (!response?.success) {
                    return; // Cancel — stay on the main window with the changes intact.
                }
                if (response.save) {
                    this.pendingWizardSource = source;
                    this.formChangeStateService.setSaveForm();
                } else {
                    this.discardMainChanges();
                    this.setRecommendationWizardSource(source);
                }
            });
    }

    closeRecommendationWizard(): void {
        this.setRecommendationWizardSource(null);
    }

    onRecommendationWizardFinish(topics: AccountIntentTopic[]): void {
        if (this.recommendationWizardSource) {
            this.onListChange(topics, this.recommendationWizardSource);
            this.trackRecommenderEvent(this.recommendationWizardSource, 'Finish');
        }
        this.setRecommendationWizardSource(null);
    }

    private discardMainChanges(): void {
        this.bomboraAccountTopics = [...this.bomboraAccountTopicsLastSaved];
        this.salesintelAccountTopics = [...this.salesintelAccountTopicsLastSaved];
        this.formChangeStateService.setFormChanged(false);
    }

    private setRecommendationWizardSource(source: IntentSourceEnum.BOMBORA | IntentSourceEnum.SALES_INTEL | null): void {
        if (source) {
            this.trackRecommenderEvent(source, 'Start');
        }
        this.recommendationWizardSource = source;
        // Host modal hides its footer (Save / Save and Close / Cancel) while the wizard is open.
        this.formChangeStateService.setWizardActive(source !== null);
        // Wizard opens from a clean working copy; reset the pending-changes flag on open and close.
        this.formChangeStateService.setWizardHasChanges(false);
        // Often reached from async callbacks (dialog close, save response); OnPush wouldn't otherwise
        // re-render, so flush the open/close (and any reverted topics) to the view now.
        this.cdr.detectChanges();
    }

    onListChange(topics: AccountIntentTopic[], source: IntentSourceEnum.BOMBORA | IntentSourceEnum.SALES_INTEL): void {
        const sortedTopics = this.orderByPipe.transform(topics, 'topic_name');
        switch (source) {
            case IntentSourceEnum.BOMBORA:
                this.bomboraAccountTopics = [...topics];
                this.formChangeStateService.setFormChanged(JSON.stringify(sortedTopics) !== JSON.stringify(this.bomboraAccountTopicsLastSaved));
                break;
            case IntentSourceEnum.SALES_INTEL:
                this.salesintelAccountTopics = [...topics];
                this.formChangeStateService.setFormChanged(JSON.stringify(sortedTopics) !== JSON.stringify(this.salesintelAccountTopicsLastSaved));
                break;
        }
    }

    private checkTopicsChanged(): void {
        if (this.selectedTab === this.tabs.indexOf(GeneralsMenuTabsEnum.BOMBORA_INTENT)) {
            const sortedTopics = this.orderByPipe.transform(this.salesintelAccountTopics, 'topic_name');
            this.formChangeStateService.setFormChanged(JSON.stringify(sortedTopics) !== JSON.stringify(this.salesintelAccountTopicsLastSaved));
        } else {
            const sortedTopics = this.orderByPipe.transform(this.bomboraAccountTopics, 'topic_name');
            this.formChangeStateService.setFormChanged(JSON.stringify(sortedTopics) !== JSON.stringify(this.bomboraAccountTopicsLastSaved));
        }
    }

    private setTopics(): void {
        combineLatest([
            this.getBomboraIntentTopics(),
            this.getSalesintelIntentTopics(),
        ]).pipe(untilDestroyed(this)).subscribe(([bomboraTopics, salesintelTopics]: [ConvertedIntentTopicInterface[], ConvertedIntentTopicInterface[]]) => {
            this.bomboraTopics = bomboraTopics;
            this.salesintelTopics = salesintelTopics;
        });
    }

    private setAccountTopics(isIntentAutomationsEnabled) {
        this.accountService.getAccountIntentTopics()
            .pipe(untilDestroyed(this))
            .subscribe((topics: AccountIntentTopic[]) => {
                const bombora = topics.filter(({ intent_source }: AccountIntentTopic) => intent_source === IntentSourceEnum.BOMBORA);
                const salesintel = topics.filter(({ intent_source }: AccountIntentTopic) => intent_source === IntentSourceEnum.SALES_INTEL);

                this.bomboraAccountTopics = [...bombora];
                this.bomboraAccountTopicsLastSaved = this.orderByPipe.transform(bombora, 'topic_name');
                this.salesintelAccountTopics = [...salesintel];
                this.salesintelAccountTopicsLastSaved = this.orderByPipe.transform(salesintel, 'topic_name');
                if (isIntentAutomationsEnabled) {
                    this.isAccountTopicsSetSubject.next(true);
                }
            });
    }

    private getAccountAddOns(): void {
        const addons = this.accountService.getAddons();
        const bombora = addons.find((intent) => intent.identifier === AddonsIdentifiersEnum.INTENT);
        const salesintel = addons.find((intent) => intent.identifier === AddonsIdentifiersEnum.SALESINTELINTENT);

        if (!bombora) {
            this.onSelect(GeneralsMenuTabsEnum.PREDICTIVEINTENT);
        }

        this.bomboraPlans = this.preparePlans(bombora);
        this.bomboraPropertyValue = bombora?.plans?.[0]?.property_values;
        this.salesintelPlans = this.preparePlans(salesintel);
        this.salesintelPropertyValue = salesintel?.plans?.[0]?.property_values;
    }

    private preparePlans(intent): any[] {
        let plans = [];
        if (intent) {
            plans.push({
                label: 'Your Plan',
                value: intent.plans[0].label
            });
            for (const prop in intent.plans[0].property_values) {
                if (intent.plans[0].property_values.hasOwnProperty(prop)) {
                    intent.properties.forEach(property => {
                        let keyValue = {};
                        if (property.identifier === prop) {
                            keyValue = {
                                label: property.label,
                                value: intent.plans[0].property_values[prop]
                            };
                            plans.push(keyValue);
                        }
                    });
                }
            }
        }
        return plans;
    }

    handleWarning(response: UnsavedChangesWarningDialogResponse, tab: MatTab, tabHeader: MatTabHeader, index: number) {
        if (response.success) {
            if (response.save) {
                this.onSave();
            } else {
                this.bomboraAccountTopics = [...this.bomboraAccountTopicsLastSaved];
                this.salesintelAccountTopics = [...this.salesintelAccountTopicsLastSaved];
            }

            this.formChangeStateService.setFormChanged(false);
            this.applyTab(tab, tabHeader, index);
        }
    }

    @LoadingWrapper(LOADING_TAG)
    private getBomboraIntentTopics(): Observable<ConvertedIntentTopicInterface[]> {
        return this.accountService.getIntentTopics(IntentSourceEnum.BOMBORA).pipe(map((intents: IntentTopicsDto[]) => this.convertIntentTopics(intents)));
    }

    @LoadingWrapper(LOADING_TAG)
    private getSalesintelIntentTopics(): Observable<ConvertedIntentTopicInterface[]> {
        return this.accountService.getIntentTopics(IntentSourceEnum.SALES_INTEL).pipe(map((intents: IntentTopicsDto[]) => this.convertIntentTopics(intents)));
    }

    @LoadingWrapper(INTENT_SETTING_MODAL_LOADING_TAG)
    private postAccountIntentTopics(topics: AccountIntentTopic[], isIntentAutomationsEnabled: boolean) {
        return this.accountService.postAccountIntentTopics(topics, isIntentAutomationsEnabled, { showError: true });
    }

    @LoadingWrapper(LOADING_TAG)
    private getIntentAutomationTopicMappingDetails(topics: IntentTopicIds): Observable<IntentTopicAutomationMapping[]> {
        return this.accountService.getIntentAutomationTopicMappingDetails(topics);
    }

    private convertIntentTopics(intents): ConvertedIntentTopicInterface[] {
        return intents?.reduce((acc: ConvertedIntentTopicInterface[], intent: IntentTopicsDto) => ([
            ...acc,
            {
                category: intent.category,
                topic_names: intent.topics.map(({ topic_name }: IntentTopic) => topic_name),
            },
            ...intent.topics,
        ]), []);
    }

    private trackRecommenderEvent(
        source: IntentSourceEnum.BOMBORA | IntentSourceEnum.SALES_INTEL,
        action: 'Start' | 'Finish'
    ): void {
        const label: string = source === IntentSourceEnum.SALES_INTEL ? 'SI' : 'Bombora';
        this.commonUtilService.churnZeroEvents(`Portal - Account Settings - ${label} Intent Recommender ${action}`);
    }
}
